import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import {LoadingButton} from "@mui/lab";
import {Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton} from "@mui/material";
import {useEffect, useState} from "react";
import {FieldValues, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {useRecoilValue} from "recoil";
import {ImportService} from "../../../../../Component/Api/Import/ImportService";
import AppDropzone from "../../../../../Component/Common/Components/FormValidationInputs/AppDropZone";
import AppSelectList from "../../../../../Component/Common/Components/FormValidationInputs/AppSelectList";
import {MDAlert} from "../../../../../Component/Common/Components/MDAlert";
import {MDBox} from "../../../../../Component/Common/Components/MDBox";
import {MDTypography} from "../../../../../Component/Common/Components/MDTypography";
import {
    ImportProgressBarWithErrorList
} from "../../../../../Component/Common/Components/ProgressBar/ImportProgressBarWithErrors";
import {ExportKeyValue} from "../../../../../Component/Model/Dto/Upload";
import {DataImportType} from "../../../../../Component/Model/Enum/BEE123Enums";
import {ImportCommonFunctionsDropDown, getUploadFileName} from "../../../../../Helpers/ImportHelper";
import {defaultObject, emptyGuid} from "../../../../../Helpers/typescriptHelper";
import {UserAtom} from "../../../../../State/user.Atom";
import {CoreImportService} from "../../../../../Component/Api/Import/CoreImportService";
import {
    VerificationWorkflowService
} from "../../../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService";
import {ImportSource} from "../../../../../Component/Model/Enum/ImportEnum";
import {useSignalRConnection} from "../../../../../Hooks/useSignalRConnection";

interface IProps {
    open: boolean;
    closeModal: () => void;
    dataImportType: DataImportType;
    organisationId: number;
}

export interface ProgressData {
    progress: number;
    numberOfRowsCorrect: number;
    numberOfRowsFailed: number;
    log: any[];
    isSuccess: boolean;
}

export const TrainingImportModal = ({ open, closeModal, dataImportType, organisationId }: IProps) => {
    const user = useRecoilValue(UserAtom);

    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [progressData, setProgressData] = useState<ProgressData>({
        progress: 0,
        numberOfRowsCorrect: 0,
        numberOfRowsFailed: 0,
        log: [],
        isSuccess: false,
    });
    const hub = useSignalRConnection("ImportHub");

    const { control, handleSubmit, watch, setValue } = useForm({
        mode: 'all',
    });

    const clearFile = () => {
        setValue('file', null);
    };

    useEffect(() => {
        (async () => {
            try {
                if (!hub.isConnected) {
                    await hub.start();
                }

                hub.on(`importProgress-${user.username}-${DataImportType[dataImportType]}`, (data: any) => {
                    setProgressData(data);
                })

                hub.on(`importProgressFailed-${user.username}-${DataImportType[dataImportType]}`, (data: any) => {
                    const {NumberOfRowsCorrect, NumberOfRowsFailed, Log} = data;
                    setProgressData({
                        ...data,
                        progress: 0,
                        isSuccess: false,
                        numberOfRowsCorrect: NumberOfRowsCorrect,
                        numberOfRowsFailed: NumberOfRowsFailed,
                        log: Log
                    });
                    toast.error("Import Failed");
                    setIsImporting(false);
                });

                hub.on(`importProgressSuccess-${user.username}-${DataImportType[dataImportType]}`, (data: any) => {
                    const {NumberOfRowsCorrect, Log} = data;
                    setProgressData({
                        ...data,
                        isSuccess: true,
                        progress: 100,
                        numberOfRowsCorrect: NumberOfRowsCorrect,
                        log: Log
                    });
                    toast.success("Import Succeeded");
                    setIsImporting(false);
                })
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();
    }, []);

    const getImportTemplate = (e: any) => {
        ImportService.DownloadImportTemplate(emptyGuid, getUploadFileName(DataImportType[dataImportType]));
    };

    const checkForInProgressImport = async (importType: number): Promise<boolean> => {
        try {
            const result = await VerificationWorkflowService.IsImportInProgressForOrganisation(organisationId, importType);
            return result.data;
        } catch (error) {
            console.log(error);
            toast.error("An error has occurred");
            return false;
        }
    };


    const onDataSubmit = async (data: FieldValues) => {
        setProgressData(defaultObject);
        setIsImporting(true);
        try {
            const parameters: ExportKeyValue[] = [
                {
                    key: 'OrganisationId',
                    value: organisationId.toString()
                },
            ];

            const isInProgress = await checkForInProgressImport(dataImportType);
            if (isInProgress){
                toast.error(`An import is already in progress. Please try again later`)
                setIsImporting(false); 
                return; 
            }

            
            const request = {
                importType: dataImportType,
                importAction: parseInt(data.importType),
                importSource: ImportSource.WSPATR,
                file: data.file,
                parameters: parameters,
                setPercentageUpload: (progress: number) => null,
            }
            await CoreImportService.ImportFile(request);
        } catch (error) {
            console.log(error);
            setIsImporting(false);
            toast.error('Import Failed');
        }
    }

    const file = watch('file');

    return (
        <Dialog open={open} onClose={() => closeModal()} maxWidth={false} aria-labelledby="form-dialog-title">
            <form
                id="JobTitleImportForm"
                onSubmit={handleSubmit(onDataSubmit)}
            >
                <DialogTitle>
                    File Upload
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={closeModal}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ paddingTop: "5px !important", width: "600px " }}>
                    <AppSelectList
                        disabled={isImporting}
                        control={control}
                        name="importType"
                        label="Import Type"
                        items={ImportCommonFunctionsDropDown(DataImportType[dataImportType])}
                    />
                    <Box mt={2}>
                        {!file && <AppDropzone control={control} name="file" width={568} isExcel={true}></AppDropzone>}
                        {file && (
                            <Box display="flex" flexDirection="column" alignItems="center">
                                <Box display="flex" justifyContent="flex-end" alignItems="flex-start">
                                    <Box position="relative">
                                        <img src={`${process.env.PUBLIC_URL}/icons/fileTypes/xlsx.svg`} alt='preview' style={{ height: '200px' }} />
                                        <IconButton
                                            disabled={isImporting}
                                            onClick={clearFile}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                zIndex: 1
                                            }}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                                <MDTypography variant="caption" align="center">{file.name}</MDTypography>
                            </Box>
                        )}
                    </Box>
                    <Box mt={2}>
                        <ImportProgressBarWithErrorList
                            value={progressData?.progress ?? 0}
                            progressData={progressData}
                        />
                    </Box>
                    <Grid item xs={5}>
                        <LoadingButton
                            loading={isImporting}
                            variant="contained"
                            color="primary"
                            form="JobTitleImportForm"
                            type="submit"
                            disabled={!file}
                        >
                            Begin Import
                        </LoadingButton>
                    </Grid>
                    <Grid item xs={12} lg={12}>
                        <MDBox mt={4} color="white">
                            <MDAlert color='light'>
                                <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>
                                    <p style={{ marginBottom: "0.2rem" }}> Click<Button variant="text" size="small" onClick={getImportTemplate} style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                                        to download the template file</p>
                                </MDTypography>
                            </MDAlert>
                        </MDBox>
                    </Grid>
                </DialogContent>
            </form>
        </Dialog>
    )
}
